import exifr from 'exifr';
import type { ImageCaptureHints } from '../types';

/**
 * Read non-sensitive capture hints from the ORIGINAL file bytes (before canvas re-encode).
 * GPS / location / owner / serial fields are never requested, returned, logged, or persisted.
 */
export async function extractCaptureHints(fileOrBlob: Blob): Promise<ImageCaptureHints | null> {
  try {
    const parsed = await exifr.parse(fileOrBlob, {
      // Explicit pick only — do not enable gps / IPTC owner fields
      pick: [
        'DateTimeOriginal',
        'CreateDate',
        'Make',
        'Model',
        'Software',
        'Orientation',
        'ImageWidth',
        'ImageHeight',
        'ExifImageWidth',
        'ExifImageHeight',
      ],
      translateKeys: false,
      reviveValues: true,
    });

    if (!parsed || typeof parsed !== 'object') return null;

    const dateRaw = parsed.DateTimeOriginal || parsed.CreateDate || null;
    let dateTimeOriginal: string | null = null;
    if (dateRaw instanceof Date && !Number.isNaN(dateRaw.getTime())) {
      dateTimeOriginal = dateRaw.toISOString();
    } else if (typeof dateRaw === 'string' && dateRaw.trim()) {
      dateTimeOriginal = dateRaw.trim();
    }

    const pixelWidth =
      numberOrNull(parsed.ExifImageWidth) ?? numberOrNull(parsed.ImageWidth) ?? null;
    const pixelHeight =
      numberOrNull(parsed.ExifImageHeight) ?? numberOrNull(parsed.ImageHeight) ?? null;

    const hints: ImageCaptureHints = {
      dateTimeOriginal,
      cameraMake: stringOrNull(parsed.Make),
      cameraModel: stringOrNull(parsed.Model),
      software: stringOrNull(parsed.Software),
      orientation: numberOrNull(parsed.Orientation),
      pixelWidth,
      pixelHeight,
    };

    const hasAny = Object.values(hints).some((v) => v != null && v !== '');
    return hasAny ? hints : null;
  } catch {
    return null;
  }
}

function stringOrNull(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t ? t : null;
}

function numberOrNull(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  return null;
}

/** Compact supporting-evidence text for the analysis model (never includes location). */
export function formatCaptureHintsForPrompt(hints: ImageCaptureHints | null | undefined): string {
  if (!hints) return '';
  const parts: string[] = [];
  if (hints.cameraMake || hints.cameraModel) {
    parts.push(`Camera: ${[hints.cameraMake, hints.cameraModel].filter(Boolean).join(' ')}`);
  }
  if (hints.software) parts.push(`Software: ${hints.software}`);
  if (hints.dateTimeOriginal) parts.push(`File date/time: ${hints.dateTimeOriginal}`);
  if (hints.pixelWidth && hints.pixelHeight) {
    parts.push(`Pixel size: ${hints.pixelWidth}×${hints.pixelHeight}`);
  }
  if (hints.orientation != null) parts.push(`Orientation tag: ${hints.orientation}`);
  return parts.join('; ');
}
